import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTranslation } from "react-i18next";

import CustomDialog from "../../../components/modals/CustomDialog";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectPreference } from "../../preferences/preferencesSlice";
import {
  BookingFollowUpStatus,
  BookingFollowUpType,
  Employee,
} from "../../process/types/processTypes";
import { bulkUpdateBookingFollowUp } from "../../process/processSlices";
import {
  ineligibleFollowUpReason,
  statusOrderForType,
} from "../utils/followUpHelpers";

const employeeName = (e: Employee) =>
  `${e.givenName ?? ""} ${e.surName ?? ""}`.trim() || "—";

interface Props {
  selectedEmployees: Employee[];
  followUpType: BookingFollowUpType;
  onClose: (saved: boolean, status?: BookingFollowUpStatus) => void;
}

export function BulkFollowUpUpdateDialog({
  selectedEmployees,
  followUpType,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const dispatch = useAppDispatch();
  const { locale } = useAppSelector(selectPreference);

  // Selectable target statuses for this follow-up type. Embassy excludes the
  // implicit "new" (pre-submission) state since you never move back to it.
  const statusOptions = useMemo(() => {
    const order = statusOrderForType(followUpType);
    return followUpType === BookingFollowUpType.embassy
      ? order.filter((s) => s !== BookingFollowUpStatus.new)
      : order;
  }, [followUpType]);

  const [status, setStatus] = useState<BookingFollowUpStatus | "">(
    statusOptions[0] ?? "",
  );
  const [scheduledDate, setScheduledDate] = useState<Date | null>(new Date());
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<{
    eligible: Employee[];
    ineligible: { employee: Employee; reason: string }[];
  } | null>(null);

  const syncPreview = useMemo(() => {
    if (followUpType === BookingFollowUpType.embassy) {
      return status === BookingFollowUpStatus.embassy_stamped
        ? t("followUps.syncEmbassyStamped")
        : null;
    }
    if (status === BookingFollowUpStatus.lmis_issued) {
      return t("followUps.syncLmisIssued");
    }
    if (status !== BookingFollowUpStatus.employee_completed) return null;
    switch (followUpType) {
      case BookingFollowUpType.medical:
        return te("completed");
      case BookingFollowUpType.tasheer:
        return t("followUps.syncBiometrics");
      case BookingFollowUpType.flight:
        return t("followUps.syncFlight");
      default:
        return null;
    }
  }, [followUpType, status, t, te]);

  const doUpdate = async (employees: Employee[]) => {
    if (!status || employees.length === 0) return;
    setSubmitting(true);
    const result = await dispatch(
      bulkUpdateBookingFollowUp({
        ids: employees
          .map((e) => String(e._id))
          .filter((id) => id && id !== "undefined"),
        type: followUpType,
        status,
        scheduledDate:
          status === BookingFollowUpStatus.booked_with_date ||
          status === BookingFollowUpStatus.lmis_issued
            ? (scheduledDate ?? undefined)
            : undefined,
        remark: remark.trim() || undefined,
      }),
    );
    setSubmitting(false);
    if (bulkUpdateBookingFollowUp.fulfilled.match(result)) {
      onClose(true, status);
    }
  };

  const handleApply = () => {
    if (!status) return;
    if (
      (status === BookingFollowUpStatus.booked_with_date ||
        status === BookingFollowUpStatus.lmis_issued) &&
      !scheduledDate
    ) {
      return;
    }

    const eligible: Employee[] = [];
    const ineligible: { employee: Employee; reason: string }[] = [];
    for (const e of selectedEmployees) {
      const reason = ineligibleFollowUpReason(status, e, followUpType);
      if (reason) ineligible.push({ employee: e, reason });
      else eligible.push(e);
    }

    if (ineligible.length === 0) {
      void doUpdate(eligible);
    } else {
      setConfirm({ eligible, ineligible });
    }
  };

  const dateRequired =
    (status === BookingFollowUpStatus.booked_with_date ||
      status === BookingFollowUpStatus.lmis_issued) &&
    !scheduledDate;

  return (
    <CustomDialog
      title={t("followUps.bulkUpdate")}
      isOpen
      handleDialogClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${selectedEmployees.length} ${t("employees")}`}
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 1,
            height: 96,
            overflowY: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 1,
          }}
        >
          {selectedEmployees.map((e, i) => (
            <Typography
              key={String(e._id)}
              variant="caption"
              component="div"
              noWrap
            >
              {`${i + 1}. ${employeeName(e)}`}
            </Typography>
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("followUps.status")}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as BookingFollowUpStatus);
                setConfirm(null);
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {te(`bookingFollowUp_${option}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {status === BookingFollowUpStatus.booked_with_date ||
          status === BookingFollowUpStatus.lmis_issued ? (
            <Grid item xs={12}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={locale}
              >
                <DatePicker
                  label={
                    status === BookingFollowUpStatus.lmis_issued
                      ? t("lmisIssuedDate")
                      : t("followUps.scheduledDate")
                  }
                  views={["year", "month", "day"]}
                  minDate={new Date("2023-01-01")}
                  disableMaskedInput
                  inputFormat="DD-MMM-YYYY"
                  value={scheduledDate}
                  onChange={(value) => setScheduledDate(value as Date | null)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" required />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          ) : null}
          {syncPreview && (
            <Grid item xs={12}>
              <Alert severity="success">
                {t("followUps.willSync", { field: syncPreview })}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              label={t("remark")}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {confirm ? (
              <Stack spacing={1.5}>
                <Alert severity="warning">
                  {`${confirm.ineligible.length} of ${selectedEmployees.length} cannot be updated to "${status ? te(`bookingFollowUp_${status}`) : ""}" and will be skipped.`}
                </Alert>
                <Box
                  sx={{
                    p: 1,
                    maxHeight: 120,
                    overflowY: "auto",
                    border: 1,
                    borderColor: "warning.light",
                    borderRadius: 1,
                  }}
                >
                  {confirm.ineligible.map(({ employee, reason }) => (
                    <Typography
                      key={String(employee._id)}
                      variant="caption"
                      component="div"
                      noWrap
                    >
                      {`${employeeName(employee)} — ${reason}`}
                    </Typography>
                  ))}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={submitting}
                    onClick={() => setConfirm(null)}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    fullWidth
                    disabled={confirm.eligible.length === 0 || submitting}
                    onClick={() => void doUpdate(confirm.eligible)}
                  >
                    <Save />
                    {`${t("save")} (${confirm.eligible.length})`}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Button
                color="secondary"
                variant="contained"
                size="large"
                fullWidth
                disabled={!status || dateRequired || submitting}
                onClick={handleApply}
              >
                <Save />
                {t("save") + " " + t("followUps.status")}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </CustomDialog>
  );
}
